import { useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import {
  Building2, FileText, MapPin, Users, Zap, Clock, ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { uahFull, dmy, ACTIVITY_TYPE_LABEL, outletsRelevant, legalSurfaceLabel } from "@/lib/client360Labels";
import { useClient360Overview } from "@/hooks/useClients";
import { useQuery } from "@tanstack/react-query";
import { fetchBusinessProfile } from "@/api/businessProfile";
import { ClassificationDrawer } from "@/components/clients/ClassificationDrawer";
import { TaskCreateDrawer } from "@/components/task/TaskCreateDrawer";
import { QuickCallDrawer } from "@/components/client360/QuickCallDrawer";
import { CreateOutletDrawer } from "@/components/clients/CreateOutletDrawer";
import { ContactCreateFlow } from "@/components/clients/ContactCreateFlow";
import { ClientBiStrip } from "@/components/clients/ClientBiStrip";
import { CustomerPurchasesSummary } from "@/components/clients/CustomerPurchasesSummary";
import type {
  Client360Envelope, Client360OverviewResponse, OverviewAction, AttentionItem,
  AttentionSeverity, NextBestAction,
} from "@/api/clients";

/**
 * Client360 — Огляд / Overview 2.0 (Этап 1).
 *
 * Built to VISUAL_Client360_Overview_v2_2_LOCKED.html (content + DS tokens; the
 * shell sidebar/topbar is the existing KormaxHome shell, not duplicated). Block
 * order: header next-line → NBA hero → BI strip(secondary) → «Потребує уваги»(3) →
 * Key Contacts + Open Tasks → snapshots(LE/outlets/contracts) → Recent Activity.
 *
 * Backend-owned (§0.3): the matrix / counts / severities / allowedActions all come
 * from GET /client360/:id/overview; this component renders verbatim and draws a CTA
 * ONLY when its action is in allowedActions (§4 CTA-honesty — no dead buttons in
 * prod). blockStatus distinguishes empty (→ CTA) from unavailable (→ honest
 * «джерело не підключено»). Type-aware (§3/§5.5): service types hide «Потребує уваги».
 */

const SEV_DOT: Record<AttentionSeverity, string> = {
  red: "bg-destructive",
  amber: "bg-warning-foreground",
  blue: "bg-info-foreground",
  green: "bg-success-foreground",
};

export function Client360Overview() {
  const env = useOutletContext<Client360Envelope>();
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? env?.header.id ?? "";
  const navigate = useNavigate();
  const query = useClient360Overview(id);

  const [classifyOpen, setClassifyOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [quickCallOpen, setQuickCallOpen] = useState(false);
  const [outletOpen, setOutletOpen] = useState(false);
  // §0-fix: contact-add = IN-CONTEXT create for THIS client (canonical ContactDrawer
  // via ContactCreateFlow), NEVER a redirect to the global /contacts list.
  const [contactCreate, setContactCreate] = useState<{ primary: boolean } | null>(null);

  if (!env) return null;

  const ov = query.data;
  const allowed = new Set<OverviewAction>(ov?.allowedActions ?? []);

  /** Map a backend action verb to its real production target (§4). */
  function run(action: OverviewAction | null, _anchor?: string | null) {
    if (!action || !allowed.has(action)) return;
    switch (action) {
      // 3A: «активність»/«дія» CTAs collapse to the operational quick call-log
      // (which can spawn a Payment Promise + follow-up). NO Activity CRUD / 3B routing.
      case "createActivity":
      case "createAction":
        setQuickCallOpen(true);
        break;
      case "classify":
        setClassifyOpen(true);
        break;
      case "openProfile":
        navigate(clientRoutes.businessProfile(id));
        break;
      case "viewContacts":
        // in-context: THIS client's «Контакти» tab (not the global list).
        navigate(clientRoutes.contacts(id));
        break;
      case "addContact":
        // in-context create — canonical ContactDrawer pre-bound to this holding.
        // NBA + attention emit addContact ONLY for «немає основного контакту»
        // (OverviewMatrixResolver no_primary_contact) → primary pre-set; the
        // generic header chip calls setContactCreate({primary:false}) directly.
        setContactCreate({ primary: true });
        break;
      case "viewLegalEntities":
        navigate(clientRoutes.entities(id));
        break;
      case "viewContracts":
        navigate(clientRoutes.contracts(id));
        break;
      case "addOutlet":
        setOutletOpen(true);
        break;
    }
  }

  return (
    <div className="space-y-4 p-5 md:p-6">
      {/* header next-line — last contact · next action · overdue (IA §3) */}
      <HeaderLine ov={ov} />

      {/* Next Best Action (hero, action-first) — backend-owned, not React-generated */}
      {ov?.nextBestAction && (
        <NbaHero nba={ov.nextBestAction} allowed={allowed} onRun={run} />
      )}

      {/* BI strip (secondary) — holding-grain, BI-owned, read-only */}
      <ClientBiStrip
        env={env}
        onCreateAction={env.allowedActions.canLogCall ? () => setQuickCallOpen(true) : undefined}
      />

      {/* PB-2 «Купує у нас» — compact (auto-BI кеш, read-only, D-049) */}
      <CustomerPurchasesSummary holdingId={id} variant="compact" />

      {/* loading / error for the lazy overview payload */}
      {query.isPending && <OverviewSkeleton />}
      {query.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <ShieldAlert className="h-4 w-4 shrink-0" /> Не вдалося завантажити деталі огляду.
          <button onClick={() => query.refetch()} className="ml-auto font-semibold underline">Повторити</button>
        </div>
      )}

      {ov && (
        <>
          {/* «Потребує уваги» — type-aware: hidden for service types (§5.5) */}
          {ov.blockStatus.attention !== "hidden" && <Attention ov={ov} onRun={run} />}

          {/* Key Contacts + Open Tasks */}
          <div className="grid gap-4 lg:grid-cols-2">
            <KeyContacts
              ov={ov}
              id={id}
              allowed={allowed}
              navigate={navigate}
              onAdd={(primary) => allowed.has("addContact") && setContactCreate({ primary })}
            />
            <OpenTasks ov={ov} id={id} canCreate={!!env.allowedActions.canCreateTask} navigate={navigate} onCreate={() => setTaskDrawerOpen(true)} />
          </div>

          {/* Snapshots — Юрособи / Точки / Договори; v1.4 type-driven (AC-A4R-011/013/014) */}
          <Snapshots
            ov={ov} id={id} allowed={allowed} navigate={navigate} onAddOutlet={() => run("addOutlet")}
            recordType={env.header.recordType}
            primaryBusinessModel={env.businessProfileSummary?.primaryBusinessModel ?? null}
            legalIdentityType={env.header.legalIdentityType}
            activeMembers={env.header.activeMembers}
          />

          {/* Recent Activity */}
          <RecentActivity ov={ov} id={id} canLog={!!env.allowedActions.canLogCall} navigate={navigate} onLog={() => setQuickCallOpen(true)} />
        </>
      )}

      <ClassificationDrawer
        open={classifyOpen}
        holdingId={id}
        header={env.header}
        businessModel={env.businessProfileSummary?.primaryBusinessModel ?? null}
        onClose={() => setClassifyOpen(false)}
      />
      <TaskCreateDrawer
        open={taskDrawerOpen}
        onClose={() => setTaskDrawerOpen(false)}
        entry={{ holdingId: id, source: "картка клієнта (Client360)", defaultSubtype: "generic" }}
      />
      <QuickCallDrawer
        open={quickCallOpen}
        onClose={() => setQuickCallOpen(false)}
        holdingId={id}
        overdueAmount={env.biStrip?.holdingOverdueTotal ?? null}
      />
      <CreateOutletDrawer
        open={outletOpen}
        holdingId={id}
        clientName={env.header.displayName || env.header.name}
        legalEntities={(ov?.snapshots.legalEntities ?? []).map((le) => ({ id: le.id, name: le.name }))}
        contacts={(ov?.keyContacts ?? []).filter((c) => c.id).map((c) => ({ id: c.id, name: c.name }))}
        onClose={() => setOutletOpen(false)}
      />
      {/* §0-fix — in-context contact create (canonical ContactDrawer, D-034 flow) */}
      <ContactCreateFlow
        open={!!contactCreate}
        holding={{ id, name: env.header.displayName || env.header.name }}
        prefillPrimary={contactCreate?.primary}
        onClose={() => setContactCreate(null)}
      />
    </div>
  );
}

// ── header next-line ────────────────────────────────────────────────────────

function HeaderLine({ ov }: { ov?: Client360OverviewResponse }) {
  const last = ov?.headerLine.lastActivity ?? null;
  const next = ov?.headerLine.nextAction ?? null;
  const overdue = ov?.headerLine.overdueCount ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 rounded-xl border bg-card px-4 py-2.5 text-[13px]">
      <span>
        <span className="text-muted-foreground">Останній контакт: </span>
        <span className="font-medium">
          {last ? `${dmy(last.date)} · ${ACTIVITY_TYPE_LABEL[last.type] ?? last.type}${last.label ? ` · ${last.label}` : ""}` : "ще немає"}
        </span>
      </span>
      <span>
        <span className="text-muted-foreground">Наступна дія: </span>
        <span className="font-medium">{next ? `${dmy(next.date)} · ${next.label || "заплановано"}` : "—"}</span>
      </span>
      <span>
        <span className="text-muted-foreground">Прострочено: </span>
        <span className={cn("font-medium", overdue > 0 && "text-destructive")}>
          {overdue > 0 ? `${overdue} задач` : "немає"}
        </span>
      </span>
    </div>
  );
}

// ── NBA hero ──────────────────────────────────────────────────────────────────

function NbaHero({ nba, allowed, onRun }: { nba: NextBestAction; allowed: Set<OverviewAction>; onRun: (a: OverviewAction | null, anchor?: string | null) => void }) {
  return (
    <div className="rounded-xl border border-primary bg-gradient-to-b from-primary/5 to-card p-4">
      <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-primary">
        <Zap className="h-4 w-4" /> Наступна найкраща дія
      </div>
      <div className="mt-1 text-base font-bold">{nba.label}</div>
      {nba.reason && <div className="mt-0.5 text-[12.5px] text-muted-foreground">Чому: {nba.reason}</div>}
      {allowed.has(nba.ctaAction) && (
        <button
          onClick={() => onRun(nba.ctaAction, nba.anchor)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          {nba.label}
        </button>
      )}
    </div>
  );
}

// ── «Потребує уваги» (3 columns) ──────────────────────────────────────────────

function ctaLabel(item: AttentionItem): string {
  switch (item.code) {
    case "no_primary_contact": return "+ Додати";
    case "profile_unverified": return "Профіль →";
    case "record_type_unknown": return "Класифікувати";
    case "overdue_debt": return "Створити дію";
    case "stale_no_activity": return "Зафіксувати";
    default: return "Відкрити";
  }
}

function Attention({ ov, onRun }: { ov: Client360OverviewResponse; onRun: (a: OverviewAction | null, anchor?: string | null) => void }) {
  const { missing, dataQuality } = ov.attention;
  return (
    <section>
      <div className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Потребує уваги</div>
      <div className="grid gap-4 md:grid-cols-3">
        <AttentionCol title="Бракує даних" items={missing} onRun={onRun} />
        <AttentionCol title="Якість даних" items={dataQuality} onRun={onRun} />
        <EnrichmentCol ov={ov} />
      </div>
    </section>
  );
}

function AttentionCol({ title, items, onRun }: { title: string; items: AttentionItem[]; onRun: (a: OverviewAction | null, anchor?: string | null) => void }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {items.length === 0 ? (
        <div className="py-2 text-[12.5px] text-muted-foreground">Без зауважень</div>
      ) : (
        items.map((it) => (
          <div key={it.code} className="flex items-center gap-2.5 border-t py-2 first:border-t-0">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", SEV_DOT[it.severity])} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px]">{it.label}</div>
              {it.detail && <div className="text-[11.5px] text-muted-foreground">{it.detail}</div>}
            </div>
            {it.ctaAction && (
              <button
                onClick={() => onRun(it.ctaAction, it.anchor)}
                className="shrink-0 rounded-md border border-primary px-2.5 py-1 text-[12px] font-semibold text-primary hover:bg-primary/5"
              >
                {ctaLabel(it)}
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function EnrichmentCol({ ov }: { ov: Client360OverviewResponse }) {
  const status = ov.blockStatus.enrichment;
  const items = ov.attention.enrichment;
  return (
    <div className="rounded-xl border bg-card p-4">
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Збагачення</h4>
      {status === "unavailable" ? (
        <div className="py-2 text-[12.5px] text-muted-foreground">Джерело не підключено</div>
      ) : items.length === 0 ? (
        <div className="py-2 text-[12.5px] text-muted-foreground">Пропозицій немає</div>
      ) : (
        items.map((it, i) => (
          <div key={i} className="flex items-center gap-2.5 border-t py-2 first:border-t-0">
            <span className="h-2 w-2 shrink-0 rounded-full bg-info-foreground" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px]">{it.suggestion}</div>
              <div className="text-[11.5px] text-muted-foreground">{it.source}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ── Key Contacts ──────────────────────────────────────────────────────────────

function CardHead({ icon: Icon, title, action }: { icon: typeof Users; title: string; action?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h3 className="text-sm font-bold">{title}</h3>
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

function ChipButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-md border border-primary px-2.5 py-1 text-[12px] font-semibold text-primary hover:bg-primary/5">
      {children}
    </button>
  );
}

function KeyContacts({ ov, id, allowed, navigate, onAdd }: {
  ov: Client360OverviewResponse; id: string; allowed: Set<OverviewAction>;
  /** primary=true для CTA «основний контакт / власника» (empty-state). */
  navigate: ReturnType<typeof useNavigate>; onAdd: (primary: boolean) => void;
}) {
  void id;
  if (ov.blockStatus.keyContacts === "forbidden") return <div className="rounded-xl border bg-card p-4 text-[13px] text-muted-foreground">Контакти недоступні (немає доступу).</div>;
  const list = ov.keyContacts;
  // §6 — type-aware missing-contact wording: breeder / фізособа-roztrib не мають
  // «закупівельника» (B2B-термін) → лишаємо «основний контакт / власника».
  const nonB2bContact = ov.recordType === "breeder_person"
    || ov.recordType === "retail_b2c_customer"
    || ov.businessModel === "breeder_professional";
  const addContactLabel = nonB2bContact
    ? "+ Додати основний контакт / власника"
    : "+ Додати власника / закупівельника";
  return (
    <div className="rounded-xl border bg-card p-4">
      <CardHead icon={Users} title="Ключові контакти" action={allowed.has("addContact") && <ChipButton onClick={() => onAdd(false)}>+ Додати контакт</ChipButton>} />
      {list.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-6 text-center text-[13px] text-muted-foreground">
          Контактів ще немає
          {/* CTA «основний/власника» → contact pre-set primary (§0-fix) */}
          {allowed.has("addContact") && <div className="mt-2"><ChipButton onClick={() => onAdd(true)}>{addContactLabel}</ChipButton></div>}
        </div>
      ) : (
        list.map((c) => (
          <button
            key={c.id || c.name}
            onClick={() => c.id && navigate(`/contacts/${c.id}`)}
            className="flex w-full items-center gap-2.5 border-t py-2 text-left first:border-t-0 hover:bg-muted/40"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
              {c.name ? c.name[0] : "?"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium">{c.name}</div>
              <div className="truncate text-[11.5px] text-muted-foreground">
                {[c.position, c.role].filter(Boolean).join(" · ")}
                {c.phone && ` · ${c.phone}`}
                {c.primary && " (осн.)"}
              </div>
            </div>
            {c.primary && <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">основний</span>}
          </button>
        ))
      )}
    </div>
  );
}

// ── Open Tasks ────────────────────────────────────────────────────────────────

function OpenTasks({ ov, id, canCreate, navigate, onCreate }: {
  ov: Client360OverviewResponse; id: string; canCreate: boolean;
  navigate: ReturnType<typeof useNavigate>; onCreate: () => void;
}) {
  if (ov.blockStatus.openTasks === "forbidden") return <div className="rounded-xl border bg-card p-4 text-[13px] text-muted-foreground">Задачі недоступні (немає доступу).</div>;
  const list = ov.openTasks;
  return (
    <div className="rounded-xl border bg-card p-4">
      <CardHead icon={FileText} title="Відкриті задачі" action={canCreate && <ChipButton onClick={onCreate}>+ Створити задачу</ChipButton>} />
      {list.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-6 text-center text-[13px] text-muted-foreground">
          Задач немає
          {canCreate && <div className="mt-2"><ChipButton onClick={onCreate}>+ Створити задачу</ChipButton></div>}
        </div>
      ) : (
        list.map((t) => (
          <div key={t.id} className="flex items-center gap-2.5 border-t py-2 first:border-t-0">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", SEV_DOT[t.severity])} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium">{t.title}</div>
              <div className="text-[11.5px] text-muted-foreground">
                {t.overdue ? "прострочено · " : ""}{t.due ? dmy(t.due) : "без терміну"}
              </div>
            </div>
            <button onClick={() => navigate(clientRoutes.tasks(id))} className="shrink-0 text-[12px] font-semibold text-primary hover:underline">Відкрити</button>
          </div>
        ))
      )}
    </div>
  );
}

// ── Snapshots ───────────────────────────────────────────────────────────────

function SrcBadge({ source }: { source: string | null }) {
  if (!source) return null;
  return <span className="shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">{source === "1С" || source === "sync_1c" ? "з 1С" : source}</span>;
}

function Snapshots({ ov, id, allowed, navigate, onAddOutlet, recordType, primaryBusinessModel, legalIdentityType, activeMembers }: {
  ov: Client360OverviewResponse; id: string; allowed: Set<OverviewAction>;
  navigate: ReturnType<typeof useNavigate>; onAddOutlet: () => void;
  recordType: string | null; primaryBusinessModel: string | null;
  legalIdentityType: string; activeMembers: number;
}) {
  const { legalEntities, outlets, contracts } = ov.snapshots;
  const canAddOutlet = allowed.has("addOutlet");

  // v1.4 AC-A4R-011/014 — type-driven «Точки»: breeder/фізособа — ховаємо
  // (та сама outletsRelevant(), що вже керує табом); dealer — лише при count>0.
  const isDealer = primaryBusinessModel === "dealer_partner";
  const showOutletsCard =
    outletsRelevant(recordType, primaryBusinessModel, legalIdentityType)
    && (!isDealer || outlets.length > 0);

  // v1.4 AC-A4R-014 — dealer: «Територія покриття» з cCDealerTerritoryRegions
  // (lazy: той самий businessProfile-запит, що й wizard — кеш спільний).
  const bpQ = useQuery({
    queryKey: ["businessProfile", id],
    queryFn: ({ signal }) => fetchBusinessProfile(id, signal),
    enabled: isDealer,
    staleTime: 60_000,
  });
  const territory: string[] = isDealer
    ? ((bpQ.data?.fields?.cCDealerTerritoryRegions as string[] | undefined) ?? [])
    : [];

  // v1.4 AC-A4R-013 — legal-контур label type-aware по (provisional)
  // legalIdentityType; needsConfirm НЕ блокує показ.
  const legalTitle = legalSurfaceLabel(legalIdentityType, activeMembers);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {/* Юрособи / ФОП / Дані особи — type-aware */}
      <div className="rounded-xl border bg-card p-4">
        <CardHead icon={Building2} title={legalTitle} action={allowed.has("viewLegalEntities") && legalEntities.length > 0 && (
          <button onClick={() => navigate(clientRoutes.entities(id))} className="text-[12px] font-semibold text-primary hover:underline">Усі →</button>
        )} />
        {legalEntities.length === 0 ? <Empty>Юрособи відсутні</Empty> : legalEntities.map((le) => (
          <button key={le.id} onClick={() => navigate(clientRoutes.legalEntity(id, le.id))} className="flex w-full items-center gap-2 border-t py-2 text-left first:border-t-0 hover:bg-muted/40">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium">{le.name}</div>
              <div className="text-[11.5px] text-muted-foreground">
                {le.debt > 0 ? `борг ${uahFull(le.debt)}` : "без боргу"}{le.contractStatus ? ` · договір ${le.contractStatus}` : ""}
              </div>
            </div>
            <SrcBadge source={le.source} />
          </button>
        ))}
      </div>

      {/* Точки — type-driven (v1.4): breeder/фізособа приховано; dealer лише при count>0 */}
      {showOutletsCard && (
        <div className="rounded-xl border bg-card p-4">
          <CardHead icon={MapPin} title="Торгові точки" action={canAddOutlet && outlets.length > 0 && (
            <ChipButton onClick={onAddOutlet}>+ Додати точку</ChipButton>
          )} />
          {outlets.length === 0 ? (
            <div className="rounded-lg border border-dashed px-4 py-6 text-center text-[13px] text-muted-foreground">
              Точок немає
              {canAddOutlet && <div className="mt-2"><ChipButton onClick={onAddOutlet}>+ Додати точку</ChipButton></div>}
            </div>
          ) : outlets.map((o) => (
            <div key={o.id} className="flex items-center gap-2 border-t py-2 first:border-t-0">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium">{o.name}</div>
                {o.city && <div className="text-[11.5px] text-muted-foreground">{o.city}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* v1.4 dealer — «Територія покриття» (cCDealerTerritoryRegions, read-only chips) */}
      {isDealer && (
        <div className="rounded-xl border bg-card p-4">
          <CardHead icon={MapPin} title="Територія покриття" />
          {bpQ.isLoading ? (
            <Empty>Завантаження…</Empty>
          ) : territory.length === 0 ? (
            <Empty>Території не вказано — додайте у профілі (Редагувати → Дилер)</Empty>
          ) : (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {territory.map((t) => (
                <span key={t} className="rounded-md border bg-muted/40 px-2 py-0.5 text-[12px]">{t}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Договори */}
      <div className="rounded-xl border bg-card p-4">
        <CardHead icon={FileText} title="Договори" action={allowed.has("viewContracts") && contracts.length > 0 && (
          <button onClick={() => navigate(clientRoutes.contracts(id))} className="text-[12px] font-semibold text-primary hover:underline">Усі →</button>
        )} />
        {contracts.length === 0 ? <Empty>Договорів немає</Empty> : contracts.map((c) => (
          <button key={c.id} onClick={() => navigate(clientRoutes.contract(id, c.id))} className="flex w-full items-center gap-2 border-t py-2 text-left first:border-t-0 hover:bg-muted/40">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium">{c.num || "Договір"}</div>
              <div className="text-[11.5px] text-muted-foreground">{[c.type, c.status].filter(Boolean).join(" · ")}</div>
            </div>
            <SrcBadge source={c.source} />
          </button>
        ))}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-4 text-center text-[12.5px] text-muted-foreground">{children}</div>;
}

// ── Recent Activity ───────────────────────────────────────────────────────────

const ACT_DOT: Record<string, string> = {
  meeting: "bg-info-foreground", call: "bg-success-foreground", task: "bg-warning-foreground", note: "bg-muted-foreground",
};

function RecentActivity({ ov, id, canLog, navigate, onLog }: {
  ov: Client360OverviewResponse; id: string; canLog: boolean;
  navigate: ReturnType<typeof useNavigate>; onLog: () => void;
}) {
  const status = ov.blockStatus.recentActivity;
  if (status === "forbidden") return null;
  const list = ov.recentActivity;
  return (
    <div className="rounded-xl border bg-card p-4">
      <CardHead icon={Clock} title="Остання активність" action={
        <button onClick={() => navigate(clientRoutes.activities(id))} className="text-[12px] font-semibold text-primary hover:underline">Уся історія →</button>
      } />
      {status === "unavailable" ? (
        <div className="py-4 text-center text-[12.5px] text-muted-foreground">Джерело не підключено</div>
      ) : list.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-6 text-center text-[13px] text-muted-foreground">
          Активностей ще немає
          {canLog && <div className="mt-2"><ChipButton onClick={onLog}>📞 Зафіксувати дзвінок</ChipButton></div>}
        </div>
      ) : (
        list.map((a) => (
          <div key={a.id} className="flex gap-3 border-t py-2.5 first:border-t-0">
            <span className="w-[78px] shrink-0 text-[11.5px] text-muted-foreground">{dmy(a.date)}</span>
            <span className={cn("mt-1 h-2.5 w-2.5 shrink-0 rounded-full", ACT_DOT[a.type] ?? "bg-muted-foreground")} />
            <div className="min-w-0 flex-1 text-[13px]">
              <span className="font-semibold">{ACTIVITY_TYPE_LABEL[a.type] ?? a.type}</span>
              {a.summary && <span> — {a.summary}</span>}
              {(a.outcome || a.source) && (
                <div className="text-[11.5px] text-muted-foreground">
                  {[a.outcome && `результат: ${a.outcome}`, a.who, a.source].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ── skeleton ──────────────────────────────────────────────────────────────────

function OverviewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-20 animate-pulse rounded-xl bg-muted" />
      <div className="grid gap-4 md:grid-cols-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-muted" />)}
      </div>
    </div>
  );
}
